<template>
  <CurrentEventDataProvider v-slot="{ designStyles }">
    <VouchersDataProvider v-slot="{ voucherBalance, isVoucherBalanceLoading }">
      <div
        v-if="isVoucherBalanceLoading"
        class="voucher-outer voucher-outer--loading"
      >
        <Spinner />
      </div>
      <div
        v-else-if="
          voucherBalance &&
          (voucherBalance.usedVouchers ||
            voucherBalance.pendingVouchers ||
            (voucherBalance.transactions &&
              voucherBalance.transactions.length > 0))
        "
        class="voucher-outer"
        :style="designStyles"
      >
        <!-- White card header -->
        <div class="voucher-header">
          <span class="voucher-header__title">{{
            $t('Components.VoucherBalance.title')
          }}</span>
          <button
            v-if="$listeners && $listeners.close"
            type="button"
            class="voucher-header__icon voucher-header__close"
            :aria-label="$t('Components.shared.Navbar.close')"
            @click="$emit('close')"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
          <div v-else class="voucher-header__icon">
            <i class="fas fa-gift" aria-hidden="true"></i>
          </div>
        </div>

        <!-- QR Code block -->
        <div class="voucher-qr-wrap">
          <div class="voucher-qr-container">
            <qrcode-vue
              v-if="qrValue"
              :value="qrValue"
              :size="140"
              render-as="svg"
              level="H"
            />
            <div v-else class="voucher-qr-placeholder">
              <i class="fas fa-qrcode" aria-hidden="true"></i>
              <p>{{ $t('Components.VoucherBalance.qrNotAvailable') }}</p>
            </div>
          </div>
          <p class="voucher-qr-hint">
            {{ $t('Components.VoucherBalance.qrHint') }}
          </p>
        </div>

        <!-- Dynamic vouchers grouped by category — one card per voucher type -->
        <div
          v-if="isDynamicVoucherEnabled && voucherCategories.length"
          class="voucher-categories"
        >
          <div class="voucher-categories__title">
            {{ $t('Components.VoucherBalance.categoriesTitle') }}
          </div>
          <div
            v-for="(cat, idx) in voucherCategories"
            :key="cat.voucherId || `cat-${idx}`"
            class="voucher-category-card"
          >
            <div class="voucher-category-card__head">
              <div class="voucher-category-card__icon">
                <i class="fas fa-ticket-alt" aria-hidden="true"></i>
              </div>
              <div class="voucher-category-card__name">
                {{
                  cat.voucherTypeName ||
                  $t('Components.VoucherBalance.voucherFallback')
                }}
              </div>
            </div>
            <div class="voucher-category-card__stats">
              <div class="voucher-category-stat voucher-category-stat--left">
                <span class="voucher-category-stat__num">{{
                  categoryLeft(cat)
                }}</span>
                <span class="voucher-category-stat__label">{{
                  $t('Views.Stage.MyProfile.vouchersLeft')
                }}</span>
              </div>
              <div class="voucher-category-stat voucher-category-stat--used">
                <span class="voucher-category-stat__num">{{
                  cat.usedVoucher ?? 0
                }}</span>
                <span class="voucher-category-stat__label">{{
                  $t('Views.Stage.MyProfile.vouchersUsed')
                }}</span>
              </div>
              <div
                v-if="cat.voucherTypeAmount"
                class="voucher-category-stat voucher-category-stat--amount"
              >
                <span
                  class="voucher-category-stat__num voucher-category-stat__num--sm"
                  >{{ cat.voucherTypeAmount }}</span
                >
                <span class="voucher-category-stat__label">{{
                  $t('Components.VoucherBalance.perUnit')
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats: Pending + Used (legacy single-voucher fallback) -->
        <div
          class="voucher-stats"
          v-else-if="!(isCoregoEnabled && isMultipleVoucher)"
        >
          <!-- Pending -->
          <div class="voucher-stat voucher-stat--pending">
            <span class="voucher-stat__number">
              <template v-if="isMultipleVoucher && isDynamicVoucherEnabled">{{
                pendingVoucher
              }}</template>
              <template v-else>{{
                voucherBalance.pendingVouchers ?? 0
              }}</template>
            </span>
            <span class="voucher-stat__label">
              <template v-if="isMultipleVoucher && isDynamicVoucherEnabled">{{
                $t('Views.Stage.MyProfile.vouchersLeft')
              }}</template>
              <template v-else>{{
                $t('Views.Stage.MyProfile.vouchersPending')
              }}</template>
            </span>
          </div>
          <!-- Used -->
          <div class="voucher-stat voucher-stat--used">
            <span class="voucher-stat__number">
              <template v-if="isMultipleVoucher && isDynamicVoucherEnabled">{{
                multipleUsedVouchers
              }}</template>
              <template v-else>{{ voucherBalance.usedVouchers ?? 0 }}</template>
            </span>
            <span class="voucher-stat__label">{{
              $t('Views.Stage.MyProfile.vouchersUsed')
            }}</span>
          </div>
        </div>

        <!-- Corrego: assigned + voucher type -->
        <div class="voucher-stats" v-if="isMultipleVoucher && isCoregoEnabled">
          <div class="voucher-stat voucher-stat--assigned">
            <span class="voucher-stat__number">{{
              voucherBalance.totalVouchers
            }}</span>
            <span class="voucher-stat__label">{{
              $t('Views.Stage.MyProfile.vouchersAssigned')
            }}</span>
          </div>
          <div v-if="voucherTypeName" class="voucher-stat voucher-stat--type">
            <span class="voucher-stat__number voucher-stat__number--sm">{{
              voucherTypeName
            }}</span>
            <span class="voucher-stat__label">{{
              $t('Views.Stage.MyProfile.voucherType')
            }}</span>
          </div>
        </div>
      </div>

      <div v-else class="voucher-outer voucher-outer--empty">
        <div class="voucher-header">
          <span class="voucher-header__title">{{
            $t('Components.VoucherBalance.title')
          }}</span>
          <button
            v-if="$listeners && $listeners.close"
            type="button"
            class="voucher-header__icon voucher-header__close"
            :aria-label="$t('Components.shared.Navbar.close')"
            @click="$emit('close')"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
          <div v-else class="voucher-header__icon">
            <i class="fas fa-gift" aria-hidden="true"></i>
          </div>
        </div>
        <div class="voucher-empty">
          <i
            class="fas fa-exclamation-circle voucher-empty__icon"
            aria-hidden="true"
          ></i>
          <p class="voucher-empty__text">
            {{ $t('Views.Stage.MyProfile.vouchersNotAvailable') }}
          </p>
        </div>
      </div>
    </VouchersDataProvider>
  </CurrentEventDataProvider>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import Spinner from '@/components/shared/Spinner';
import QrcodeVue from 'qrcode.vue';
import { getPusherInstance } from '@/utils';
export default {
  name: 'ProfileInfo',
  components: {
    Spinner,
    QrcodeVue
  },
  data() {
    return { windowWidth: window.innerWidth };
  },
  created() {
    if (this.isMultipleVoucher) {
      this.getDynamicVoucherAsync({
        registrationId: this.profile.UserID,
        eventId: this.getEvent.eventId
      });
    } else {
      this.getVouchers();
    }

    this.pusher = getPusherInstance({ isReady: true });
    this.pusher.bind(
      `voucher-change-${this.profile.TicketQrCode}`,
      this.onVoucherChange
    );
  },
  beforeUnmount() {
    this.pusher?.unbind(
      `voucher-change-${this.profile.TicketQrCode}`,
      this.onVoucherChange
    );
  },

  methods: {
    ...mapActions('Vouchers', ['getVouchers', 'getDynamicVoucherAsync']),

    onVoucherChange() {
      if (this.isMultipleVoucher) {
        this.getDynamicVoucherAsync({
          registrationId: this.profile.UserID,
          eventId: this.getEvent.id
        });
      } else {
        this.getVouchers();
      }
    }
  },
  computed: {
    ...mapGetters('CurrentEvent', ['getEvent', 'features']),
    ...mapGetters('Vouchers', {
      dynamicVouchers: 'getDynamicVouchers',
      isDynamicVoucherLoading: 'getDynamicVoucherLoading'
    }),
    ...mapGetters('Authentication', {
      profile: 'getUser'
    }),

    voucherTypeName() {
      return this.dynamicVouchers?.[0]?.voucherTypeName ?? '';
    },

    pendingVoucher() {
      return (
        this.dynamicVouchers?.[0]?.balanceLeft /
          this.dynamicVouchers?.[0]?.voucherTypeAmount ?? 0
      );
    },

    multipleUsedVouchers() {
      return this.dynamicVouchers?.[0]?.usedVoucher ?? 0;
    },

    voucherCategories() {
      // Each entry in dynamicVouchers represents a voucher category
      // (voucherTypeName + balanceLeft + usedVoucher). Render them as
      // separate cards so the attendee sees all categories the admin
      // configured, not just the first one.
      const list = Array.isArray(this.dynamicVouchers)
        ? this.dynamicVouchers
        : [];
      return list.filter(Boolean);
    },

    categoryLeft() {
      return (cat) => {
        if (!cat) return 0;
        const amount = Number(cat.voucherTypeAmount);
        const left = Number(cat.balanceLeft);
        if (!Number.isFinite(amount) || amount <= 0) return left || 0;
        if (!Number.isFinite(left)) return 0;
        return Math.floor(left / amount);
      };
    },

    isMultipleVoucher() {
      return Boolean(this.getEvent?.isMultipleVoucher);
    },

    isCoregoEnabled() {
      return Boolean(this.getEvent?.isCoregoEnabled);
    },

    isDynamicVoucherEnabled() {
      return Boolean(this.getEvent?.isDynamicVoucher);
    },

    qrCodeSize() {
      return Math.min(Math.max(parseInt(this.windowWidth / 2), 50), 200);
    },

    qrValue() {
      console.log('inside qr value', this.profile);
      // Multiple-voucher events: use the dynamic voucher ID
      if (this.isMultipleVoucher) {
        console.log('this.isMultipleVoucher', this.dynamicVouchers);
        const vid = this.dynamicVouchers?.[0]?.voucherId;
        return vid != null && vid !== '' ? String(vid) : null;
      }
      // Standard vouchers: TicketQrCode is preferred
      console.log('this.profile', this.profile);
      if (this.profile?.TicketQrCode) {
        return this.profile.TicketQrCode;
      }
      // Fallback: UserID uniquely identifies the attendee at redemption points
      // (same identifier the voucher balance API uses)
      const uid = this.profile?.UserID;
      return uid != null && uid !== '' ? String(uid) : null;
    },
    isQrEnabled() {
      const event = this.getEvent;
      return (
        this.profile &&
        this.profile.TicketQrCode &&
        event.features.stageQrTicketsEnabled
      );
    }
  }
};
</script>

<style scoped lang="scss">
.voucher-outer {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;

  &--loading,
  &--empty {
    min-height: 200px;
    justify-content: flex-start;
  }
}

.voucher-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
  }

  &__close {
    border: none;
    padding: 0;
    cursor: pointer;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 15px;
  }
}

.voucher-qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.voucher-qr-container {
  background: #f3f4f6;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voucher-qr-placeholder {
  width: 140px;
  height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #d1d5db;

  i {
    font-size: 48px;
  }

  p {
    font-size: 14px;
    color: #6b7280;
    text-align: center;
    margin: 0;
  }
}

.voucher-qr-hint {
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  line-height: 1.5;
  max-width: 220px;
}

.voucher-categories {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }
}

.voucher-category-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    background: #ffffff;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__stats {
    display: flex;
    gap: 8px;
  }
}

.voucher-category-stat {
  flex: 1;
  border-radius: 10px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: #ffffff;
  border: 1px solid #f3f4f6;

  &--left {
    background: #ecfdf5;
    border-color: transparent;
  }

  &--used {
    background: #f3f4f6;
    border-color: transparent;
  }

  &--amount {
    background: #eff6ff;
    border-color: transparent;
  }

  &__num {
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
    color: #111827;

    .voucher-category-stat--left & {
      color: #15803d;
    }
    .voucher-category-stat--used & {
      color: #6b7280;
    }
    .voucher-category-stat--amount & {
      color: #2563eb;
    }

    &--sm {
      font-size: 16px;
    }
  }

  &__label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    .voucher-category-stat--left & {
      color: #15803d;
    }
    .voucher-category-stat--amount & {
      color: #2563eb;
    }
  }
}

.voucher-stats {
  display: flex;
  gap: 10px;
}

.voucher-stat {
  flex: 1;
  border-radius: 12px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  &--pending {
    background: #ecfdf5;
  }

  &--used {
    background: #f3f4f6;
  }

  &--assigned {
    background: #eff6ff;
  }

  &--type {
    background: #f3f4f6;
  }

  &__number {
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
    color: var(--color-primary, #047873);

    .voucher-stat--used & {
      color: #111827;
    }

    .voucher-stat--assigned & {
      color: #2563eb;
    }

    &--sm {
      font-size: 16px;
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-primary, #047873);

    .voucher-stat--used & {
      color: #6b7280;
    }

    .voucher-stat--assigned & {
      color: #2563eb;
    }
  }
}

.voucher-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 10px;
  padding: 24px 0;

  &__icon {
    font-size: 28px;
    color: #d1d5db;
  }

  &__text {
    font-size: 14px;
    color: #6b7280;
    text-align: center;
    font-weight: 500;
  }
}
</style>
